<template>
  <!-- Mobile Slide-up Modal -->
  <div v-if="isMobile && isVisible" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
    <div class="w-full bg-[#ABBCF4] rounded-t-3xl transform transition-transform duration-300 ease-out max-h-[90vh] flex flex-col"
         :class="isVisible ? 'translate-y-0' : 'translate-y-full'">

      <!-- Mobile Content -->
      <div class="flex-1 overflow-y-auto">

      <!-- Search Form -->
      <div v-if="!showResults" class="h-full flex flex-col">
        <!-- Header Section with Close Button -->
        <div class="px-6 py-6 pt-4 relative">
          <!-- Close Button -->
          <button
            @click="close"
            class="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all z-10"
          >
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          <div class="flex items-center mb-6">
            <h2 class="text-3xl font-bold text-white">Find Metro</h2>
          </div>
        </div>

        <!-- Metro Logo Section -->
        <div class="flex-1 flex items-center justify-center px-6">
          <div class="text-center">
            <img src="/metro.svg" alt="Metro" class="h-24 w-24 mx-auto mb-4 opacity-80" />
            <div class="text-white text-opacity-70 text-lg font-medium">Search Metro Routes</div>
            <div class="text-white text-opacity-50 text-sm mt-2">Find metro routes in your city</div>
          </div>
        </div>

        <!-- Search Inputs Section - Bottom -->
        <div class="px-6 pb-6">
          <!-- Error message -->
          <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {{ error }}
          </div>

          <!-- Station inputs -->
          <div class="space-y-4">
            <!-- From station input -->
            <div class="relative">
              <input
                type="text"
                v-model="fromStation"
                @input="handleFromStationInput"
                @focus="handleFromStationFocus"
                @blur="hideFromSuggestions"
                placeholder="Enter departure station"
                class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
              />
              <!-- From Station Suggestions -->
              <div v-if="showFromSuggestions && fromSuggestions.length > 0"
                class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto mt-1">
                <div v-for="station in fromSuggestions"
                  :key="station"
                  @mousedown="selectFromStation(station)"
                  class="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                  <span class="text-sm text-gray-800">{{ station }}</span>
                </div>
              </div>
            </div>

            <!-- Swap stations button - positioned between inputs -->
            <div class="relative flex justify-center z-20">
              <button
                @click="swapStations"
                class="bg-teal-500 text-white rounded-full p-2 shadow-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 border-2 border-white transition-all duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  />
                </svg>
              </button>
            </div>

            <!-- To station input -->
            <div class="relative">
                <input
                  type="text"
                  v-model="toStation"
                  @input="handleToStationInput"
                  @focus="handleToStationFocus"
                  @blur="hideToSuggestions"
                  placeholder="Enter destination station"
                  class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
                />
                <!-- To Station Suggestions -->
                <div v-if="showToSuggestions && toSuggestions.length > 0"
                  class="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  <div v-for="station in toSuggestions"
                    :key="station"
                    @mousedown="selectToStation(station)"
                    class="p-2 hover:bg-teal-50 cursor-pointer">
                    {{ station }}
                  </div>
                </div>
            </div>

            <!-- Search button -->
            <button @click="searchMetro" :disabled="isLoading || !fromStation || !toStation" class="w-full bg-blue-500 text-white py-3 rounded-3xl font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed">
              <span v-if="isLoading" class="flex items-center justify-center">
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </span>
              <span v-else>Search Metro</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Results View (shown after search) -->
      <div v-else class="h-full">
        <!-- Header at the very top -->
        <div class="flex items-center justify-between px-4 py-4 bg-[#ABBCF4] sticky top-0 z-20 rounded-t-3xl">
          <button @click="showResults = false" class="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <h2 class="text-xl font-bold text-white">Metro Routes</h2>
          <!-- Close Button -->
          <button
            @click="close"
            class="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
          >
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Error message -->
        <div v-if="error" class="bg-red-50 text-red-600 p-4 rounded-lg mb-4 mx-4">
          {{ error }}
        </div>

        <!-- Metro Results Component -->
        <div class="px-4 pb-4">
          <MetroResultsComponent
            :from="fromStation"
            :to="toStation"
            :routes="metroResults"
            :is-loading="isLoading"
            :error="error"
            @retry="showResults = false"
          />
        </div>
      </div>
      </div>
    </div>
  </div>

  <!-- Desktop/Inline Mode -->
  <div v-else-if="isInline || (!isMobile && isVisible)"
       class="h-full bg-[#ABBCF4] rounded-3xl overflow-hidden">
    <!-- Search Form -->
    <div v-if="!showResults" class="h-full flex flex-col">
      <!-- Header Section -->
      <div class="px-6 py-6">
        <div class="flex items-center mb-6">
          <h2 class="text-3xl font-bold text-white">Find Metro</h2>
        </div>
      </div>

      <!-- Metro Logo Section -->
      <div class="flex-1 flex items-center justify-center px-6">
        <div class="text-center">
          <img src="/metro.svg" alt="Metro" class="h-24 w-24 mx-auto mb-4 opacity-80" />
          <div class="text-white text-opacity-70 text-lg font-medium">Search Metro Routes</div>
          <div class="text-white text-opacity-50 text-sm mt-2">Find metro routes in your city</div>
        </div>
      </div>

      <!-- Search Inputs Section - Bottom -->
      <div class="px-6 pb-6">
        <!-- Error message -->
        <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {{ error }}
        </div>

        <!-- Station inputs -->
        <div class="space-y-4">
          <!-- From station input -->
          <div class="relative">
            <input
              type="text"
              v-model="fromStation"
              @input="handleFromStationInput"
              @focus="handleFromStationFocus"
              @blur="hideFromSuggestions"
              placeholder="Enter departure station"
              class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
            />
            <!-- From Station Suggestions -->
            <div v-if="showFromSuggestions && fromSuggestions.length > 0"
              class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto mt-1">
              <div v-for="station in fromSuggestions"
                :key="station"
                @mousedown="selectFromStation(station)"
                class="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                <span class="text-sm text-gray-800">{{ station }}</span>
              </div>
            </div>
          </div>

          <!-- Swap stations button - positioned between inputs -->
          <div class="relative flex justify-center z-20">
            <button
              @click="swapStations"
              class="bg-teal-500 text-white rounded-full p-2 shadow-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 border-2 border-white transition-all duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                />
              </svg>
            </button>
          </div>

          <!-- To station input -->
          <div class="relative">
              <input
                type="text"
                v-model="toStation"
                @input="handleToStationInput"
                @focus="handleToStationFocus"
                @blur="hideToSuggestions"
                placeholder="Enter destination station"
                class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
              />
              <!-- To Station Suggestions -->
              <div v-if="showToSuggestions && toSuggestions.length > 0"
                class="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                <div v-for="station in toSuggestions"
                  :key="station"
                  @mousedown="selectToStation(station)"
                  class="p-2 hover:bg-teal-50 cursor-pointer">
                  {{ station }}
                </div>
              </div>
          </div>

          <!-- Search button -->
          <button @click="searchMetro" :disabled="isLoading || !fromStation || !toStation" class="w-full bg-blue-500 text-white py-3 rounded-3xl font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed">
            <span v-if="isLoading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Searching...
            </span>
            <span v-else>Search Metro</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Results View (shown after search) -->
    <div v-else class="px-4 py-4 h-full overflow-hidden flex flex-col">
      <div class="flex items-center justify-between mb-4">
        <button @click="showResults = false" class="text-blue-500 p-2 hover:bg-blue-50 rounded-lg transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
        </button>
        <h2 class="text-xl font-bold text-gray-800">Metro Routes</h2>
        <div class="w-9"></div> <!-- Spacer for alignment -->
      </div>

      <!-- Error message -->
      <div v-if="error" class="bg-red-50 text-red-600 p-4 rounded-lg mb-4">
        {{ error }}
      </div>

      <div class="flex-1 overflow-y-auto">
        <MetroResultsComponent
          :from="fromStation"
          :to="toStation"
          :routes="metroResults"
          :is-loading="isLoading"
          :error="error"
          @retry="showResults = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { searchMetro } from '@/services/metroService';
import MetroResultsComponent from './MetroResultsComponent.vue';
import { metroSuggestions } from '@/utils/suggestions';
import { getExactStationName, findBestMatch, formatStationForAPI } from '@/utils/stationUtils';
import { stationSwapMixin } from '@/mixins/stationSwapMixin';

export default {
  name: 'MetroSearchPopup',
  mixins: [stationSwapMixin],
  components: {
    MetroResultsComponent
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true
    },
    isInline: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'search'],
  data() {
    return {
      fromStation: '',
      toStation: '',
      isLoading: false,
      error: null,
      showResults: false,
      metroResults: [],
      fromSuggestions: [],
      toSuggestions: [],
      showFromSuggestions: false,
      showToSuggestions: false
    };
  },
  methods: {
    close() {
      if (!this.isInline) {
        this.$emit('close');
        this.error = null;
        this.showResults = false;
        this.metroResults = [];
        this.fromStation = '';
        this.toStation = '';
      }
    },
    handleFromStationInput() {
      if (this.fromStation.trim()) {
        this.fromSuggestions = metroSuggestions.filter(station =>
          station.toLowerCase().includes(this.fromStation.toLowerCase())
        );
      } else {
        this.fromSuggestions = [];
      }
      this.showFromSuggestions = true;
    },
    handleToStationInput() {
      if (this.toStation.trim()) {
        this.toSuggestions = metroSuggestions.filter(station =>
          station.toLowerCase().includes(this.toStation.toLowerCase())
        );
      } else {
        this.toSuggestions = [];
      }
      this.showToSuggestions = true;
    },
    handleFromStationFocus() {
      this.showFromSuggestions = true;
      this.handleFromStationInput();
    },
    handleToStationFocus() {
      this.showToSuggestions = true;
      this.handleToStationInput();
    },
    hideFromSuggestions() {
      setTimeout(() => {
        this.showFromSuggestions = false;
      }, 200);
    },
    hideToSuggestions() {
      setTimeout(() => {
        this.showToSuggestions = false;
      }, 200);
    },
    selectFromStation(station) {
      this.fromStation = station;
      this.showFromSuggestions = false;
    },
    selectToStation(station) {
      this.toStation = station;
      this.showToSuggestions = false;
    },
    async searchMetro() {
      if (!this.fromStation || !this.toStation) {
        this.error = 'Please enter both departure and destination stations.';
        return;
      }

      // Validate and get exact station names using fuzzy matching
      const validFromStation = this.getValidatedStation(this.fromStation);
      const validToStation = this.getValidatedStation(this.toStation);

      if (!validFromStation) {
        this.error = `"${this.fromStation}" is not a valid station. Please select from the suggestions.`;
        return;
      }

      if (!validToStation) {
        this.error = `"${this.toStation}" is not a valid station. Please select from the suggestions.`;
        return;
      }

      if (validFromStation.toLowerCase() === validToStation.toLowerCase()) {
        this.error = 'Departure and destination stations cannot be the same.';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        // Format station names for API call (lowercase with hyphens)
        const formattedFromStation = formatStationForAPI(validFromStation);
        const formattedToStation = formatStationForAPI(validToStation);

        const result = await searchMetro(formattedFromStation, formattedToStation);

        if (result && result.success && result.route) {
          this.metroResults = [result.route];
          
          // Update the input fields with validated station names
          this.fromStation = validFromStation;
          this.toStation = validToStation;
          
          this.showResults = true;
          this.$emit('search', {
            from: validFromStation,
            to: validToStation,
            results: result
          });
        } else {
          this.metroResults = [];
          this.error = result?.message || 'No metro routes found for this route.';
          this.showResults = true;
        }
      } catch (error) {
        if (error.message && error.message.includes('Failed to fetch')) {
          this.error = 'Network error: Could not connect to the metro service. Please try again later.';
        } else {
          this.error = error.message || 'Failed to fetch metro data. Please try again.';
        }
        this.metroResults = [];
        this.showResults = true;
      } finally {
        this.isLoading = false;
      }
    },
    getValidatedStation(stationName) {
      if (!stationName) return null;

      // First try to get exact match
      const exactMatch = getExactStationName(stationName, metroSuggestions);
      if (exactMatch) return exactMatch;

      // If no exact match, try fuzzy matching
      const fuzzyMatch = findBestMatch(stationName, metroSuggestions, 3);
      return fuzzyMatch;
    },

  }
};
</script>

<style scoped>
.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}
.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
